import { useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { useListPatientDiagnosticReportsQuery } from '@lifeomic/phc-web-toolkit/src/graphql/queries/__generated__/ListPatientDiagnosticReports.graphql';
import { GetDiagnosticReportDocument } from '@lifeomic/phc-web-toolkit/src/graphql/queries/__generated__/GetDiagnosticReport.graphql';
import {
  useServiceAuth,
  useActiveProjectId,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useCreateDiagnosticReportMutation } from '@lifeomic/phc-web-toolkit/src/graphql/mutations/__generated__/CreateDiagnosticReport.graphql';
import { useDeleteTaskMutation } from '@lifeomic/phc-web-toolkit/src/graphql/mutations/__generated__/DeleteTask.graphql';
import { GetTaskDocument } from '@lifeomic/phc-web-toolkit/src/graphql/queries/__generated__/GetTask.graphql';
import { GetObservationDocument } from '@lifeomic/phc-web-toolkit/src/graphql/queries/__generated__/GetObservation.graphql';
import { useUpdateDiagnosticReportMutation } from '@lifeomic/phc-web-toolkit/src/graphql/mutations/__generated__/UpdateDiagnosticReport.graphql';
import { useDeleteObservationMutation } from '@lifeomic/phc-web-toolkit/src/graphql/mutations/__generated__/DeleteObservation.graphql';
import {
  DiagnosticReportResourceType,
  ExtensionIn,
} from '@lifeomic/phc-web-toolkit/src/util/graphql/api.graphql';
import DiagnosticReport = fhir.DiagnosticReport;
import { useApolloClient } from '@apollo/client';
import _omit from 'lodash/omit';

interface UseGenomicsReportsProps {
  datasetId: string;
  subjectId: string;
  isVariantSelectionEnabled: boolean;
  dateQuery?: string;
}

export const useGenomicsReports = (props: UseGenomicsReportsProps) => {
  const { subjectId, isVariantSelectionEnabled } = props;
  const [genomicsReports, setGenomicsReports] = useState<DiagnosticReport[]>(
    [],
  );

  const projectId = useActiveProjectId();
  const apolloClient = useApolloClient();

  const serviceAuth = useServiceAuth();
  const context = {
    headers: {
      'LifeOmic-Account': serviceAuth.account,
      Authorization: `Bearer ${serviceAuth.authToken}`,
    },
  };
  // it will cause infinite re-rendering w/o useMemo
  const dateQuery = useMemo(
    () => props.dateQuery || `le${DateTime.now().toISO()}`,
    [props.dateQuery],
  );

  const { refetch } = useListPatientDiagnosticReportsQuery({
    fetchPolicy: 'cache-first',
    context,
    variables: { dateQuery, subjectId },
    skip: !isVariantSelectionEnabled,
    onCompleted: data => {
      const reports = data?.patient?.diagnosticReports.edges.map(edge => {
        const node = edge.node;
        return {
          id: node.id,
          status: node.status,
          code: {
            coding: node.code.coding,
          },
          effectiveDateTime: node.effectiveDateTime,
          result: node.result,
        };
      });
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setGenomicsReports(reports);
    },
  });

  const [createDiagnosticReportMutation] = useCreateDiagnosticReportMutation({
    context,
  });

  const [updateDiagnosticReportMutation] = useUpdateDiagnosticReportMutation({
    context,
  });

  const [deleteObservationMutation] = useDeleteObservationMutation({
    context,
  });

  const [deleteTaskMutation] = useDeleteTaskMutation({
    context,
  });

  const updateDiagnosticReport = async (
    diagnosticReport: DiagnosticReport,
  ): Promise<void> => {
    const resourcesReleased = await releaseResources(diagnosticReport);

    if (!resourcesReleased) {
      await updateDiagnosticReportMutation({
        variables: {
          diagnosticReport: {
            code: {
              coding: [{ system: 'http://loinc.org', code: '81247-9' }],
            },
            id: diagnosticReport.id,
            resourceType: DiagnosticReportResourceType.DiagnosticReport,
            result: (diagnosticReport.result || []).map(ref => {
              return { reference: ref.reference };
            }),
          },
        },
      });
    }
  };

  // if a variant has been unselected and Added:
  //   - delete associated genetic observation fhir resource
  //   - delete genetic assessment task fhir resource associated with the observation
  //   - leave all other references intact
  const releaseResources = async (diagnosticReport: DiagnosticReport) => {
    const { data } = await apolloClient.query({
      query: GetDiagnosticReportDocument,
      variables: { id: diagnosticReport.id },
      context,
    });

    if (!data || !data.diagnosticReport || !data.diagnosticReport.result)
      return false;

    // get existing observations not in the new observations
    let oldRef: any[] = [];
    if (!diagnosticReport.result) {
      oldRef = data.diagnosticReport.result;
    } else {
      oldRef = data.diagnosticReport.result.filter(
        (item1: any) =>
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          !diagnosticReport.result.some(
            item2 => item1.reference === item2.reference,
          ),
      );
    }

    let deleteObservationId: string[] = [];
    const deleteTaskId: string[] = [];
    let updatedExtensions: any[] = [];
    if (oldRef.length > 0) {
      // ensure observations targeted for delete exist and are coded as genetic variant assessment
      const oldObservation = await getGeneticObservation(
        oldRef.map(r => r.reference),
      );

      // get ids of observations
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      deleteObservationId = oldObservation.map(o => o.id);

      // get ids of tasks in existing DiagnosticReport
      const oldTaskId = data.diagnosticReport.extension
        .filter((t: any) => t.valueReference)
        .map((t: any) => t.valueReference.reference.split('/')[1]);

      if (oldTaskId.length > 0) {
        // retrieve genetic assessment tasks only
        const oldTasks = await getGeneticAssessmentTask(oldTaskId);

        // get task ids no longer being referenced
        oldTasks.forEach(t => {
          if (t.input) {
            const inputs = t.input;
            inputs.forEach(i => {
              if (i.valueReference?.reference) {
                const refs = i.valueReference.reference;
                const match = refs.split('/');
                if (match.length === 2 && match[0] === 'Observation') {
                  if (deleteObservationId.includes(match[1])) {
                    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                    deleteTaskId.push(t.id);
                  }
                }
              }
            });
          }
        });
      }

      // delete orphaned genetic observations by id
      if (deleteObservationId.length > 0)
        await deleteObservation(deleteObservationId);

      // delete orphaned genetic task assessments by id
      if (deleteTaskId.length > 0) {
        await deleteTask(deleteTaskId);

        // remove associated extensions in DiagnosticReport that reference back to a deleted observation
        if (data.diagnosticReport.extension) {
          updatedExtensions = data.diagnosticReport.extension.filter(
            (e: any) => {
              if (!e.valueReference) return true;
              else if (
                deleteTaskId.includes(e.valueReference.reference.split('/')[1])
              )
                return false;
              else return true;
            },
          );
        }
      }

      const variables = {
        variables: {
          diagnosticReport: {
            code: {
              coding: [{ system: 'http://loinc.org', code: '81247-9' }],
            },
            id: diagnosticReport.id,
            resourceType: DiagnosticReportResourceType.DiagnosticReport,
            result: (diagnosticReport.result || []).map(ref => {
              return { reference: ref.reference };
            }),
            ...(updatedExtensions.length > 0 && {
              extension: updatedExtensions.map(extension =>
                _omit(extension, '__typename', 'valueReference.__typename'),
              ) as ExtensionIn[],
            }),
          },
        },
      };

      await updateDiagnosticReportMutation(variables);
      return true;
    } else {
      return false;
    }
  };

  const newGenomicReport = async () => {
    await createDiagnosticReportMutation({
      variables: {
        effectiveDateTime: DateTime.now().toISO(),
        subjectRef: `Patient/${subjectId}`,
        datasetId: projectId,
      },
    });
  };

  const deleteObservation = async (ids: string[]) => {
    const result = ids.map(async id => {
      return deleteObservationMutation({
        variables: {
          id: id,
        },
      });
    });
    await Promise.all(result);
  };

  const getGeneticAssessmentTask = async (
    ids: string[],
  ): Promise<fhir.Task[]> => {
    const task = await getTask(ids);
    const geneticAssessmentTask = task.filter(t => {
      if (!t.input) return false;
      else
        return t.input.some(i => {
          if (!i.type || !i.type.coding) return false;
          else if (i.type.coding.find(obj => obj['code'] === '69548-6'))
            return true;
          else return false;
        });
    });
    return geneticAssessmentTask;
  };

  const getTask = async (ids: string[]): Promise<fhir.Task[]> => {
    const promise = ids.map(async id => {
      return apolloClient.query({
        query: GetTaskDocument,
        variables: { id: id },
        context,
      });
    });
    const result: fhir.Task[] = [];
    await Promise.all(promise).then(tasks => {
      tasks.forEach(x => {
        if (x.data.task) result.push(x.data.task);
      });
    });
    return result;
  };

  const getGeneticObservation = async (
    ids: string[],
  ): Promise<fhir.Observation[]> => {
    const observation = await getObservation(ids.map(id => id.split('/')[1]));
    const geneticObservation = observation.filter(o => {
      if (!o.code || !o.code.coding) return false;
      else if (o.code.coding.find(obj => obj['code'] === '69548-6'))
        return true;
      else return false;
    });
    return geneticObservation;
  };

  const getObservation = async (ids: string[]): Promise<fhir.Observation[]> => {
    const promise = ids?.map(async id => {
      return apolloClient.query({
        query: GetObservationDocument,
        variables: { id: id },
        context,
      });
    });
    const result: fhir.Observation[] = [];
    await Promise.all(promise).then(observations => {
      observations.forEach(x => {
        if (x.data.observation) result.push(x.data.observation);
      });
    });
    return result;
  };

  const deleteTask = async (ids: string[]) => {
    const result = ids.map(async id => {
      return deleteTaskMutation({
        variables: {
          id: id,
        },
      });
    });
    await Promise.all(result);
  };

  return {
    genomicsReports,
    refreshGenomicsReports: refetch,
    newGenomicReport,
    updateDiagnosticReport,
  };
};
